import { auth, db } from "../firebase";
import { collection, doc, getDoc, getDocs, query, runTransaction, where } from "firebase/firestore";
import { langNgheFirestore } from "../lib/langNgheFirestore";

const col = () => collection(db, "yeuCauSuaChuVuonVuon");
const lockCol = () => collection(db, "yeuCauSuaPendingLock");

/** Khoá 1 pending / (loai, targetId) — chặn race 2 tab gửi cùng lúc. */
function lockId(loai, targetId) {
  return `${loai}__${targetId}`;
}

/**
 * Field form Chủ vườn được phép đề xuất sửa (không gồm định danh bất biến).
 * Ngân hàng/STK/chủ TK cũng chờ admin duyệt — không lưu thẳng.
 */
export const FIELD_CHO_PHEP_CHU_VUON = [
  "hoTen",
  "ngaySinh",
  "sdt",
  "loaiChuVuon",
  "ngayCapCccd",
  "noiCapCccd",
  "email",
  "diaChi",
  "tinh",
  "tinhMa",
  "xa",
  "xaMa",
  "thon",
  "ghiChu",
  "tenNganHang",
  "soTkNganHang",
  "chuTaiKhoan",
];

/** Field form Vườn được phép đề xuất sửa (không gồm id / maChuVuon chủ sở hữu). */
export const FIELD_CHO_PHEP_VUON = [
  "tenVuon",
  "tinh",
  "xa",
  "thon",
  "diaChiChiTiet",
  "dienTichHa",
  "namBatDauTrong",
  "soCay",
  "giongBo",
  "maSoVungTrong",
  "chungNhan",
  "ghiChu",
];

const FIELD_KHOA = new Set(["id", "cccd", "cccdHash", "cccdMask", "maChuVuon", "createdAt"]);

function fieldChoPhepTheoLoai(loai) {
  if (loai === "chuVuon") return FIELD_CHO_PHEP_CHU_VUON;
  if (loai === "vuon") return FIELD_CHO_PHEP_VUON;
  throw new Error("loai phải là chuVuon hoặc vuon");
}

function collectionTarget(loai) {
  return loai === "chuVuon" ? "chuVuon" : "vuon";
}

/** Lọc patch: chỉ field whitelist, bỏ định danh bất biến. */
export function locDuLieuMoi(loai, patch) {
  const choPhep = new Set(fieldChoPhepTheoLoai(loai));
  const out = {};
  for (const [k, v] of Object.entries(patch ?? {})) {
    if (FIELD_KHOA.has(k) || !choPhep.has(k)) continue;
    out[k] = v;
  }
  return out;
}

/** Field thực sự đổi so với snapshot hiện tại (so sánh string hoá). */
export function chiFieldThayDoi(cu, moi) {
  const out = {};
  for (const [k, v] of Object.entries(moi ?? {})) {
    const a = cu?.[k] ?? "";
    const b = v ?? "";
    if (String(a) !== String(b)) out[k] = v;
  }
  return out;
}

/** Snapshot các field đề xuất (cùng bộ key với duLieuMoi) để admin so sánh. */
function layDuLieuCu(loai, docData, keysMoi) {
  const choPhep = new Set(fieldChoPhepTheoLoai(loai));
  const keys = keysMoi?.length ? keysMoi : [...choPhep];
  const out = {};
  for (const k of keys) {
    if (!choPhep.has(k) || FIELD_KHOA.has(k)) continue;
    out[k] = docData?.[k] ?? null;
  }
  return out;
}

function mapDoc(d) {
  return { id: d.id, ...d.data() };
}

async function ganTenLienQuan(dsTho) {
  const uidList = [...new Set(dsTho.map((r) => r.nguoiGui).filter(Boolean))];
  const targetChuVuon = [
    ...new Set(
      dsTho
        .filter((r) => r.loai === "chuVuon")
        .map((r) => r.targetId)
        .filter(Boolean)
    ),
  ];
  const targetVuon = [
    ...new Set(
      dsTho
        .filter((r) => r.loai === "vuon")
        .map((r) => r.targetId)
        .filter(Boolean)
    ),
  ];

  const [nguoiDocs, cvDocs, vuonDocs] = await Promise.all([
    Promise.all(uidList.map((id) => getDoc(doc(db, "nguoiDung", id)))),
    Promise.all(targetChuVuon.map((id) => getDoc(doc(db, "chuVuon", id)))),
    Promise.all(targetVuon.map((id) => getDoc(doc(db, "vuon", id)))),
  ]);

  const tenNguoi = Object.fromEntries(
    nguoiDocs.map((d) => [d.id, d.exists() ? d.data()?.hoTen ?? d.data()?.email ?? null : null])
  );
  const tenChuVuon = Object.fromEntries(
    cvDocs.map((d) => [d.id, d.exists() ? d.data()?.hoTen ?? null : null])
  );
  const tenVuon = Object.fromEntries(
    vuonDocs.map((d) => [d.id, d.exists() ? d.data()?.tenVuon ?? null : null])
  );

  return dsTho.map((r) => ({
    ...r,
    tenNguoiGui: tenNguoi[r.nguoiGui] ?? null,
    tenTarget: r.loai === "chuVuon" ? tenChuVuon[r.targetId] ?? null : tenVuon[r.targetId] ?? null,
  }));
}

function sapXepMoiNhatTruoc(ds) {
  return [...ds].sort((a, b) =>
    String(b.thoiGianGui || "").localeCompare(String(a.thoiGianGui || ""))
  );
}

export const yeuCauSuaChuVuonVuonApi = {
  /**
   * NV gửi yêu cầu sửa document đã có.
   * @param {{ loai: "chuVuon"|"vuon", targetId: string, duLieuMoi: object }}
   * @returns {Promise<{ id: string, ...fields }>}
   */
  async guiYeuCau({ loai, targetId, duLieuMoi }) {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("Chưa đăng nhập");
    if (loai !== "chuVuon" && loai !== "vuon") {
      throw new Error("loai phải là chuVuon hoặc vuon");
    }
    const tid = String(targetId ?? "").trim();
    if (!tid) throw new Error("Thiếu targetId");

    const patch = locDuLieuMoi(loai, duLieuMoi);
    if (Object.keys(patch).length === 0) {
      throw new Error("Không có field hợp lệ để gửi duyệt");
    }

    const snap = await getDoc(doc(db, collectionTarget(loai), tid));
    if (!snap.exists()) {
      throw new Error(loai === "chuVuon" ? "Không tìm thấy chủ vườn" : "Không tìm thấy vườn");
    }

    const data = {
      loai,
      targetId: tid,
      duLieuCu: layDuLieuCu(loai, snap.data(), Object.keys(patch)),
      duLieuMoi: patch,
      trangThai: "cho_duyet",
      nguoiGui: uid,
      thoiGianGui: new Date().toISOString(),
      nguoiDuyet: null,
      thoiGianDuyet: null,
      lyDoTuChoi: null,
    };

    // Transaction + lock doc: 2 tab gửi cùng lúc chỉ 1 yêu cầu cho_duyet tồn tại.
    const yeuCauRef = doc(col());
    const khoaRef = doc(lockCol(), lockId(loai, tid));
    await runTransaction(db, async (tx) => {
      const lockSnap = await tx.get(khoaRef);
      if (lockSnap.exists()) {
        const dangKhoa = lockSnap.data();
        const cuRef = dangKhoa?.yeuCauId ? doc(col(), dangKhoa.yeuCauId) : null;
        const cuSnap = cuRef ? await tx.get(cuRef) : null;
        if (cuSnap?.exists() && cuSnap.data().trangThai === "cho_duyet") {
          throw new Error(
            "Đã có yêu cầu sửa đang chờ duyệt cho mục này — chờ admin duyệt hoặc từ chối trước."
          );
        }
        // Lock mồ côi (yêu cầu đã duyệt/xoá) — gỡ rồi tạo mới.
        tx.delete(khoaRef);
      }

      tx.set(yeuCauRef, data);
      tx.set(khoaRef, {
        yeuCauId: yeuCauRef.id,
        loai,
        targetId: tid,
        nguoiGui: uid,
        thoiGianGui: data.thoiGianGui,
      });
    });

    return { id: yeuCauRef.id, ...data };
  },

  /** Admin: mọi yêu cầu (mọi trạng thái). */
  async danhSachTatCa() {
    const snap = await getDocs(col());
    const ds = sapXepMoiNhatTruoc(snap.docs.map(mapDoc));
    return ganTenLienQuan(ds);
  },

  /** Admin: chỉ chờ duyệt. */
  async danhSachChoDuyet() {
    const snap = await getDocs(query(col(), where("trangThai", "==", "cho_duyet")));
    const ds = sapXepMoiNhatTruoc(snap.docs.map(mapDoc));
    return ganTenLienQuan(ds);
  },

  /** NV: chỉ yêu cầu của mình. */
  async danhSachCuaToi() {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("Chưa đăng nhập");
    const snap = await getDocs(query(col(), where("nguoiGui", "==", uid)));
    const ds = sapXepMoiNhatTruoc(snap.docs.map(mapDoc));
    return ganTenLienQuan(ds);
  },

  langNgheDanhSachTatCa(onChange, onError) {
    return langNgheFirestore(col(), {
      mapAsync: async (raw) => ganTenLienQuan(sapXepMoiNhatTruoc(raw ?? [])),
      onChange,
      onError,
    });
  },

  langNgheChoDuyet(onChange, onError) {
    return langNgheFirestore(query(col(), where("trangThai", "==", "cho_duyet")), {
      mapAsync: async (raw) => ganTenLienQuan(sapXepMoiNhatTruoc(raw ?? [])),
      onChange,
      onError,
    });
  },

  langNgheCuaToi(onChange, onError) {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      onChange([]);
      return () => {};
    }
    return langNgheFirestore(query(col(), where("nguoiGui", "==", uid)), {
      mapAsync: async (raw) => ganTenLienQuan(sapXepMoiNhatTruoc(raw ?? [])),
      onChange,
      onError,
    });
  },

  /**
   * Admin duyệt: transaction áp duLieuMoi vào chuVuon/vuon + set da_duyet.
   * Spark-only (client SDK, không Cloud Functions).
   */
  async duyet(id) {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("Chưa đăng nhập");
    if (!id) throw new Error("Thiếu id yêu cầu");

    await runTransaction(db, async (tx) => {
      const yeuCauRef = doc(db, "yeuCauSuaChuVuonVuon", id);
      const yeuCauSnap = await tx.get(yeuCauRef);
      if (!yeuCauSnap.exists()) throw new Error("Không tìm thấy yêu cầu");
      const yc = yeuCauSnap.data();
      if (yc.trangThai !== "cho_duyet") {
        throw new Error("Yêu cầu không còn ở trạng thái chờ duyệt");
      }

      const patch = locDuLieuMoi(yc.loai, yc.duLieuMoi);
      if (Object.keys(patch).length === 0) {
        throw new Error("Yêu cầu không có field hợp lệ để áp dụng");
      }

      const targetRef = doc(db, collectionTarget(yc.loai), yc.targetId);
      const targetSnap = await tx.get(targetRef);
      if (!targetSnap.exists()) {
        throw new Error(
          yc.loai === "chuVuon" ? "Chủ vườn đích không còn tồn tại" : "Vườn đích không còn tồn tại"
        );
      }

      const khoaRef = doc(lockCol(), lockId(yc.loai, yc.targetId));
      await tx.get(khoaRef);

      tx.update(targetRef, patch);
      tx.update(yeuCauRef, {
        trangThai: "da_duyet",
        nguoiDuyet: uid,
        thoiGianDuyet: new Date().toISOString(),
        lyDoTuChoi: null,
      });
      tx.delete(khoaRef);
    });
  },

  /**
   * Admin từ chối (không đụng chuVuon/vuon).
   * @param {string} id
   * @param {{ lyDoTuChoi?: string }} [opts]
   */
  async tuChoi(id, { lyDoTuChoi } = {}) {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("Chưa đăng nhập");
    if (!id) throw new Error("Thiếu id yêu cầu");

    const lyDo = lyDoTuChoi != null ? String(lyDoTuChoi).trim() : "";

    await runTransaction(db, async (tx) => {
      const yeuCauRef = doc(db, "yeuCauSuaChuVuonVuon", id);
      const yeuCauSnap = await tx.get(yeuCauRef);
      if (!yeuCauSnap.exists()) throw new Error("Không tìm thấy yêu cầu");
      const yc = yeuCauSnap.data();
      if (yc.trangThai !== "cho_duyet") {
        throw new Error("Yêu cầu không còn ở trạng thái chờ duyệt");
      }

      const khoaRef = doc(lockCol(), lockId(yc.loai, yc.targetId));
      await tx.get(khoaRef);

      tx.update(yeuCauRef, {
        trangThai: "tu_choi",
        nguoiDuyet: uid,
        thoiGianDuyet: new Date().toISOString(),
        lyDoTuChoi: lyDo || null,
      });
      tx.delete(khoaRef);
    });
  },
};
